import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Platform } from '@ionic/angular';
import { SessionDetails } from '@libs/shared/interfaces/auth';
import { CORE_EVENT_SESSION_DETAILS, CoreEvents } from '@libs/shared/interfaces/core-events';
import { isCuid } from '@paralleldrive/cuid2';
import { lastValueFrom } from 'rxjs';

import { ImageType } from '../interfaces/awesome-image.config';
import { ImageModel } from '../interfaces/awesome-image.model';
import { ApiUrlService } from './api-url-service';

export const IMAGES_DIR = 'images';
export const MEAL_IMAGES_UPLOAD_CACHE_DIR = 'meal_images_upload_cache';
export const TEMPORARY_MEAL_LOGGING_IMAGES_DIR = 'temporary_meal_logging_images';

export enum ImagesToLoad {
  Preload = 'preload',
  Full = 'full',
  Both = 'both',
}

const APP_DIRECTORY = Capacitor.getPlatform() === 'ios' ? Directory.LibraryNoCloud : Directory.Library;

@Injectable({
  providedIn: 'root',
})
export class ImageLoaderService {
  directory = APP_DIRECTORY;

  public constructor(
    protected readonly coreEventsSessionDetails: CoreEvents<SessionDetails | null>,
    protected readonly http: HttpClient,
    protected readonly platform: Platform,
    protected readonly url: ApiUrlService,
  ) {
    this.setUpImagesDir();
  }

  public async renderImage(
    imageId: string,
    type: ImageType,
    renderer: (preload: boolean, src: string) => any,
    imagesToLoad: ImagesToLoad = ImagesToLoad.Both,
    onImageError?: () => any,
  ): Promise<void> {
    const image = new ImageModel({ imageId, type }, this.url);

    const preloadUrl = image.urls.preload!;
    const imageUrl = image.urls[image.type]!;

    if (this.platform.is('hybrid')) {
      try {
        if (imagesToLoad !== ImagesToLoad.Full) {
          // Load only if not only 'full' is requested
          const preloadImageSrc = await this.loadImageNatively(image.id, preloadUrl);
          renderer(true, preloadImageSrc);
        }

        if (imagesToLoad !== ImagesToLoad.Preload) {
          // Load only if not only 'preload' is requested
          const imageSrc = await this.loadImageNatively(image.id, imageUrl);
          renderer(false, imageSrc);
        }
      } catch (error: unknown) {
        console.error('Error loading image natively', error);
        if (onImageError) {
          onImageError();
        }
      }
    } else {
      try {
        if (imagesToLoad !== ImagesToLoad.Full) {
          // Load only if not only 'full' is requested
          const preloadBlob = await lastValueFrom(this.http.get(preloadUrl, { responseType: 'blob' }));
          const preloadObjectUrl = window.URL.createObjectURL(preloadBlob as Blob);
          renderer(true, preloadObjectUrl);
        }

        if (imagesToLoad !== ImagesToLoad.Preload) {
          // Load only if not only 'preload' is requested
          const blob = await lastValueFrom(this.http.get(imageUrl, { responseType: 'blob' }));
          const url = window.URL.createObjectURL(blob as Blob);
          renderer(false, url);
        }
      } catch (error: unknown) {
        console.error('Error loading image', error);
        if (onImageError) {
          onImageError();
        }
      }
    }
  }

  private async loadImageNatively(imageId: string, imageUrl: string): Promise<string> {
    const path = `${IMAGES_DIR}/${imageId}_${imageUrl.split('?').pop()}`;

    try {
      const sessionDetails = this.coreEventsSessionDetails.getValue(CORE_EVENT_SESSION_DETAILS);

      if (!sessionDetails) {
        return Promise.reject();
      }

      // check upload image cache
      try {
        const statResult = await Filesystem.stat({
          path: `${MEAL_IMAGES_UPLOAD_CACHE_DIR}/${imageId}`,
          directory: this.directory,
        });

        return Capacitor.convertFileSrc(statResult.uri);
      } catch {}

      // check temporary image dir
      try {
        const statResult = await Filesystem.stat({
          path: `${TEMPORARY_MEAL_LOGGING_IMAGES_DIR}/${imageId}`,
          directory: this.directory,
        });

        return Capacitor.convertFileSrc(statResult.uri);
      } catch {}

      // check downloaded images
      try {
        const statResult = await Filesystem.stat({
          path,
          directory: this.directory,
        });

        return Capacitor.convertFileSrc(statResult.uri);
      } catch {}

      if (imageId.startsWith('c') || isCuid(imageId)) {
        const downloadResult = await Filesystem.downloadFile({
          path,
          url: imageUrl,
          directory: this.directory,
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${sessionDetails.token}:${sessionDetails.password}`,
          },
        });

        if (!downloadResult.path) {
          return Promise.reject('Download failed, no path returned');
        }

        return Capacitor.convertFileSrc(downloadResult.path);
      }

      return Promise.reject('Locally cached image not found: ' + imageId);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private async setUpImagesDir(): Promise<void> {
    try {
      await Filesystem.stat({
        path: IMAGES_DIR,
        directory: this.directory,
      });
    } catch {
      await Filesystem.mkdir({
        path: IMAGES_DIR,
        directory: this.directory,
        recursive: true,
      });
    }
  }
}
